import { useQuery } from '@tanstack/react-query'
import { parseAPPostUri, isSafeHostname } from '@/lib/apUtils'
import { stripHTML } from '@/services/postsService'
import { sanitizeHtml } from '@/lib/sanitizeHtml'

const FETCH_TIMEOUT_MS = 10_000

interface APContent {
    html: string
    text: string
}

async function fetchMastodonStatusContent(
    instanceHost: string,
    statusId: string
): Promise<APContent> {
    if (!isSafeHostname(instanceHost)) {
        throw new Error(`Unsafe hostname: ${instanceHost}`)
    }

    const url = `https://${instanceHost}/api/v1/statuses/${encodeURIComponent(statusId)}`
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const res = await fetch(url, { signal: controller.signal })
        if (!res.ok) throw new Error(`Mastodon API error: ${res.status}`)
        const status = (await res.json()) as { content?: string }
        const raw = status.content ?? ''
        return { html: sanitizeHtml(raw), text: stripHTML(raw) }
    } finally {
        clearTimeout(timeout)
    }
}

/**
 * Parse an AP profile URL (e.g., https://cosocial.ca/@evan) into instance + username.
 */
function parseAPProfileUrl(url: string): { instanceHost: string; username: string } | null {
    try {
        const parsed = new URL(url)
        if (!isSafeHostname(parsed.hostname)) return null
        // Match /@username or /users/username
        const match =
            parsed.pathname.match(/^\/@([^/]+)\/?$/) ??
            parsed.pathname.match(/^\/users\/([^/]+)\/?$/)
        if (!match) return null
        return { instanceHost: parsed.hostname, username: match[1] }
    } catch {
        return null
    }
}

async function fetchMastodonProfileBio(instanceHost: string, username: string): Promise<APContent> {
    if (!isSafeHostname(instanceHost)) {
        throw new Error(`Unsafe hostname: ${instanceHost}`)
    }

    const acct = `${username}@${instanceHost}`
    const url = `https://${instanceHost}/api/v1/accounts/lookup?acct=${encodeURIComponent(acct)}`
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const res = await fetch(url, { signal: controller.signal })
        if (!res.ok) throw new Error(`Mastodon API error: ${res.status}`)
        const account = (await res.json()) as { note?: string }
        const raw = account.note ?? ''
        return { html: sanitizeHtml(raw), text: stripHTML(raw) }
    } finally {
        clearTimeout(timeout)
    }
}

/**
 * Fetch the full ActivityPub content for a bridged AT Protocol post or profile.
 *
 * Bridgy Fed truncates AP content to fit AT Protocol limits.
 * This hook fetches the original full content from the Mastodon/AP instance's
 * REST API and compares it against the truncated text.
 *
 * @param originalUrl - The original AP URL (post URL or profile URL)
 * @param currentText - The current (truncated) text, used to detect if AP content is longer
 * @param enabled - Only fetch when true (lazy — set to true when user requests full content)
 */
export function useFullAPContent(
    originalUrl: string | undefined,
    currentText: string,
    enabled: boolean
) {
    const parsedPost = originalUrl ? parseAPPostUri(originalUrl) : null
    const parsedProfile = !parsedPost && originalUrl ? parseAPProfileUrl(originalUrl) : null
    const canFetch = !!(parsedPost ?? parsedProfile)

    const query = useQuery({
        queryKey: ['fullAPContent', originalUrl],
        queryFn: () => {
            if (parsedPost) {
                return fetchMastodonStatusContent(parsedPost.instanceHost, parsedPost.postId)
            }
            return fetchMastodonProfileBio(parsedProfile!.instanceHost, parsedProfile!.username)
        },
        enabled: canFetch && enabled,
        staleTime: 10 * 60 * 1000,
        gcTime: 15 * 60 * 1000,
        retry: false,
    })

    const result = query.data ?? null
    const fullContent = result?.text ?? null
    const fullContentHtml = result?.html ?? null
    const hasMore = fullContent !== null && fullContent.length > currentText.length

    return {
        fullContent,
        fullContentHtml,
        isLoading: query.isLoading,
        isError: query.isError,
        hasMore,
        canFetch,
    }
}
